import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const specialties = [
  {
    id: 'family',
    label: 'Family Medicine',
    description: '8 note sub-types for the most common visit patterns in primary care.',
    subtypes: ['General Visit', 'Diabetes Management', 'Cholesterol / Hyperlipidemia', 'GLP-1 / Weight Management', 'Prenatal Care', 'Stress & Mental Health', 'Immunization', 'Obesity Counseling'],
    sample: {
      s: 'Patient is a 52-year-old male presenting for routine diabetes follow-up. Reports improved dietary habits, no hypoglycemic episodes. Current medications: metformin 1000mg BID.',
      a: 'Type 2 Diabetes Mellitus — well-controlled. Last A1C 6.9% (3 months ago).',
      p: 'Continue metformin. Repeat A1C in 3 months. Reinforce dietary education. Referral to ophthalmology for annual diabetic eye exam.',
    },
  },
  {
    id: 'peds',
    label: 'Pediatrics',
    description: 'New patient, follow-up, counseling, and specialized autism assessment notes.',
    subtypes: ['New Patient', 'Follow-Up', 'Counseling', 'Autism – New Assessment', 'Autism – Follow-Up'],
    sample: {
      s: '4-year-old male, new patient. Parents report speech delay, limited eye contact, repetitive behaviours (lining up toys). No regression. Full-term birth, no complications.',
      a: 'Developmental concerns consistent with Autism Spectrum Disorder. M-CHAT-R/F elevated. Formal assessment warranted.',
      p: 'Referral to developmental pediatrics. Audiology assessment to rule out hearing loss. Community speech-language therapy referral. Follow up in 6 weeks.',
    },
  },
  {
    id: 'endo',
    label: 'Endocrinology',
    description: '6 templates covering thyroid conditions and both types of diabetes.',
    subtypes: ['General Consultation', 'Follow-Up', 'Type 1 Diabetes', 'Type 2 Diabetes', 'Hypothyroidism', 'Hyperthyroidism'],
    sample: {
      s: 'Patient presents with fatigue, cold intolerance, weight gain of 8 lbs over 3 months, constipation. Denies hair loss or palpitations.',
      a: 'Hypothyroidism — new diagnosis. TSH 11.2 mIU/L (elevated), Free T4 0.7 ng/dL (low).',
      p: 'Initiate levothyroxine 50mcg daily. Repeat TSH and Free T4 in 6–8 weeks. Titrate to symptom resolution and TSH 1–2 mIU/L.',
    },
  },
  {
    id: 'physio',
    label: 'Physiotherapy',
    description: 'Standard, WSIB workplace injury, and ICF framework notes.',
    subtypes: ['General Assessment', 'WSIB (Workplace Injury)', 'ICF Framework'],
    sample: {
      s: 'Patient reports lower back pain for 6 weeks following lifting injury at work. Pain 6/10, worse with prolonged sitting. WSIB claim #22-8841.',
      a: 'Lumbar strain — work-related (L4-L5 level). Functional limitation in bending and prolonged static postures.',
      p: 'Therapeutic exercise program: core stabilization, hip strengthening. Manual therapy x2/week for 4 weeks. Ergonomic advice. WSIB Form 8 completed.',
    },
  },
  {
    id: 'internal',
    label: 'Internal Medicine',
    description: 'SOAP, Impression & Plan, and Subjective-only note formats.',
    subtypes: ['Full SOAP', 'Impression & Plan', 'Subjective Only'],
    sample: {
      s: 'Patient is a 68-year-old female with known heart failure (EF 35%) presenting with 3 days of worsening dyspnea and bilateral ankle edema. Reports 4 kg weight gain.',
      a: 'Acute decompensated heart failure. Likely precipitant: dietary non-compliance (high sodium intake over holidays).',
      p: 'IV furosemide 40mg, strict fluid restriction, daily weights. Echo ordered. Cardiology consult requested. Consider uptitration of sacubitril/valsartan once euvolemic.',
    },
  },
  {
    id: 'more',
    label: '+ More Coming',
    description: 'Allergy & Immunology and Pediatric Cardiology are live. Coming soon: Psychiatry, OB/GYN, Dermatology, Emergency Medicine, Orthopedics, and more.',
    subtypes: ['Allergy & Immunology ✓', 'Pediatric Cardiology ✓', 'Lactation Consultant ✓', 'Psychiatry (soon)', 'OB/GYN (soon)', 'Dermatology (soon)', 'Emergency Medicine (soon)', 'Orthopedics (soon)'],
    sample: null,
  },
];

const soapColors = {
  s: 'bg-blue-50 border-blue-200 text-blue-800',
  a: 'bg-amber-50 border-amber-200 text-amber-800',
  p: 'bg-green-50 border-green-200 text-green-800',
};

const soapLabels = { s: 'Subjective', a: 'Assessment', p: 'Plan' };

export default function SpecialtyTabs() {
  const [active, setActive] = useState('family');
  const current = specialties.find((s) => s.id === active);

  return (
    <section className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <motion.div
          className="text-center max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <p className="section-label">Specialties</p>
          <h2 className="mt-3 section-heading">Built for your specialty, not a generic template</h2>
          <p className="section-sub">
            8 medical specialties with note templates tailored to your most common visit patterns.
            13+ more on the way.
          </p>
        </motion.div>

        {/* Tab bar */}
        <div className="mt-10 flex flex-wrap justify-center gap-2">
          {specialties.map((s) => (
            <button
              key={s.id}
              onClick={() => setActive(s.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                active === s.id
                  ? 'bg-primary-600 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Content panel */}
        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3 }}
            className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6"
          >
            {/* Left: description + sub-types */}
            <div className="bg-gray-50 rounded-2xl p-7 border border-gray-100">
              <h3 className="text-xl font-semibold text-gray-900">{current.label}</h3>
              <p className="mt-2 text-gray-500 text-sm leading-relaxed">{current.description}</p>
              <div className="mt-5">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
                  Note types
                </p>
                <div className="flex flex-wrap gap-2">
                  {current.subtypes.map((sub) => (
                    <span
                      key={sub}
                      className="px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-xs text-gray-600 font-medium"
                    >
                      {sub}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: sample note or coming soon */}
            <div className="bg-gray-50 rounded-2xl p-7 border border-gray-100">
              {current.sample ? (
                <>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-4">
                    Sample output
                  </p>
                  <div className="space-y-3">
                    {Object.entries(current.sample).map(([key, text]) => (
                      <div key={key} className={`rounded-lg border px-4 py-3 ${soapColors[key]}`}>
                        <p className="text-[10px] font-bold uppercase tracking-widest mb-1 opacity-60">
                          {soapLabels[key]}
                        </p>
                        <p className="text-sm leading-relaxed">{text}</p>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-center py-8">
                  <div className="h-12 w-12 rounded-full bg-primary-50 flex items-center justify-center mb-4">
                    <svg className="h-6 w-6 text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </div>
                  <p className="text-gray-600 font-medium">More specialties in progress</p>
                  <p className="mt-1 text-sm text-gray-400">
                    Request your specialty at{' '}
                    <a href="mailto:contact@pulsestack.ai" className="text-primary-600 hover:underline">
                      contact@pulsestack.ai
                    </a>
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
